/**
 * GLB 에셋 파이프라인 (Phase B).
 * 카탈로그 def에 `model`(파일명)이 있으면 부팅 시 `public/assets/models/`에서 GLB를
 * 프리로드해 프리미티브 bbox에 맞춰 스케일·정렬한다. 실패하거나 없으면 절차적 모델로 폴백.
 * 물리 콜라이더·파괴 파편은 기존처럼 프리미티브 bbox 기반이라 그대로 동작한다.
 *
 * 사용법: `src/config/objects.ts`의 def에 `model: 'monitor.glb'`를 추가하고 동일 파일을
 * `public/assets/models/`에 두면 자동으로 해당 모델을 사용한다(없으면 절차적).
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { BreakableObjectDef } from '../core/types';
import { boundingBoxOf } from '../physics/fracture';

const loader = new GLTFLoader();

/** def.id → 화면 발광 설정(해당 머티리얼을 어두운 베이스 + 파란 emissive로). */
const SCREEN_GLOW: Record<string, { material: string; color: number }> = {
  monitor: { material: 'metal', color: 0x2f7bff },
  macbook: { material: 'metal', color: 0x2f7bff },
};

function applyScreenGlow(root: THREE.Object3D, id: string): void {
  const cfg = SCREEN_GLOW[id];
  if (!cfg) return;
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mat = o.material;
    if (mat instanceof THREE.MeshStandardMaterial && mat.name === cfg.material) {
      const glow = mat.clone();
      glow.color = new THREE.Color(0x0a1020);
      glow.emissive = new THREE.Color(cfg.color);
      glow.emissiveIntensity = 1.0;
      glow.roughness = 0.25;
      o.material = glow;
    }
  });
}

export async function preloadModels(defs: BreakableObjectDef[]): Promise<Map<string, THREE.Group>> {
  const map = new Map<string, THREE.Group>();
  const base = import.meta.env.BASE_URL || '/';
  await Promise.all(
    defs
      .filter((d) => d.model)
      .map(async (d) => {
        try {
          const gltf = await loader.loadAsync(`${base}assets/models/${d.model}`);
          applyScreenGlow(gltf.scene, d.id);
          map.set(d.id, fitToBox(gltf.scene, boundingBoxOf(d.primitive)));
        } catch (e) {
          console.warn(`[modelLoader] "${d.id}" GLB 로드 실패 → 절차적 폴백`, e);
        }
      }),
  );
  return map;
}

/**
 * GLTF 씬을 bbox 크기에 맞춰 정렬. 얇은 축(depth)에 눌려 작아지지 않도록 **최대 치수 기준**
 * 으로 스케일하고, X/Z는 중앙, 바닥(min-Y)은 책상면(-bbox_h/2)에 올려 붙인다.
 */
function fitToBox(scene: THREE.Object3D, bbox: [number, number, number]): THREE.Group {
  scene.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(scene);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxBbox = Math.max(bbox[0], bbox[1], bbox[2]);
  const maxModel = Math.max(size.x, size.y, size.z) || 1;
  const s = maxBbox / maxModel;

  scene.scale.setScalar(s);
  scene.position.set(
    -center.x * s,
    -bbox[1] / 2 - (center.y - size.y / 2) * s, // 모델 바닥을 책상면에 정렬
    -center.z * s,
  );
  scene.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });

  const g = new THREE.Group();
  g.add(scene);
  return g;
}
